import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { useMemo, useState } from "react";

import { Card } from "@/components/ui/card";
import { Code2, Edit, Trash, Unplug } from "lucide-react";
import { useTranslation } from "react-i18next";

import { FunnelType, Upsell } from "@/@types/Upsell";
import CTATable from "@/components/shared/TableListCustom/CTATable";
import TableListCustom from "@/components/shared/TableListCustom/TableListCustom";
import useModalStore from "@/store/useModalStore";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useUpsells } from "../hooks/useUpsell";
import SideQuickScript from "./SideQuickScript";

import { ProductDetailsResponse, ProductRole } from '@/@types/Products';
import Upsells from "@/assets/icons/Upsells";
import DialogDelete from "@/components/shared/DialogDelete/DialogDelete";
import CustomSheet from "@/components/shared/Sheet/Sheet";
import { CustomizeButtonsSheet } from './CustomizeButtonsSheet';
import { DisconnectedUpsellsDialog } from './DisconnectedUpsellsDialog';
import { EditUpsellSheet } from "./EditUpsellSheet";
import { useFunnelData } from "@/hooks/useFunnelData";
import { buildFunnelGraph } from "@/pages/EditProduct/FunnelMap/utils/buildFunnelGraph";
import { usePermissions } from "@/auth/PermissionsContext";

export default function UpsellList() {
  const { t } = useTranslation(['upsells', 'common', 'funnel']);
  const navigate = useNavigate();
  const { id: productId } = useParams<{ id: string }>();
  const [selectedUpsell, setSelectedUpsell] = useState<Upsell | null>(null);
  const { modals, openModal, closeModal } = useModalStore();
  const [currentPage, setCurrentPage] = useState(1);
  const [showDisconnected, setShowDisconnected] = useState(false);

  const { product } = useOutletContext<{ product: ProductDetailsResponse }>();

  const { hasPermission } = usePermissions();
  const isProducer = product?.relationship?.role === ProductRole.PRODUCER;
  const canCreate = isProducer && hasPermission('upsells.create_upsell');
  const canEdit = isProducer && hasPermission('upsells.edit_upsell');
  const canDelete = isProducer && hasPermission('upsells.delete_upsell');
  const canCustomizeButtons = isProducer && hasPermission('upsells.customize_upsell_buttons');
  const canViewScript = hasPermission('upsells.view_upsell_integration_script');

  const { upsells, isLoading, updateUpsell, isUpdating, deleteUpsell } =
    useUpsells(productId!, { page: currentPage });

  const { upsells: allUpsells } = useUpsells(productId!, { perPage: 100 });

  const { funnelData, invalidate: invalidateFunnel } = useFunnelData(productId!);

  const { disconnectedUpsells, connectedUpsells } = useMemo(() => {
    const all = allUpsells?.data || [];
    if (!funnelData) return { disconnectedUpsells: all, connectedUpsells: [] };

    const { nodes } = buildFunnelGraph(funnelData);
    const funnelIds = new Set(
      nodes
        .filter(n => n.type === 'upsellNode')
        .map(n => n.id.replace('upsell-', ''))
    );

    return {
      disconnectedUpsells: all.filter(u => !funnelIds.has(String(u.id))),
      connectedUpsells: all.filter(u => funnelIds.has(String(u.id))),
    };
  }, [allUpsells?.data, funnelData]);

  const connectUpsells = async (
    sourceUpsellId: string,
    direction: 'accept' | 'decline',
    target: { type: 'upsell'; upsellPage: string },
  ) => {
    const field = direction === 'accept' ? 'accepted_page' : 'recused_page';
    const sourceUpsell = allUpsells?.data?.find(u => String(u.id) === sourceUpsellId);
    if (!sourceUpsell) return;

    await updateUpsell({
      productId: productId!,
      upsellId: sourceUpsellId,
      data: {
        name: sourceUpsell.name,
        funnel_type: sourceUpsell.funnel_type,
        page: sourceUpsell.page,
        offer_id: sourceUpsell.offer?.id,
        accepted_page: sourceUpsell.accepted_page ?? null,
        recused_page: sourceUpsell.recused_page ?? null,
        up_sell_display_text: sourceUpsell.up_sell_display_text,
        up_sell_button_color: sourceUpsell.up_sell_button_color,
        up_sell_display_text_color: sourceUpsell.up_sell_display_text_color,
        down_sell_display_text: sourceUpsell.down_sell_display_text,
        down_sell_button_color: sourceUpsell.down_sell_button_color,
        down_sell_display_text_color: sourceUpsell.down_sell_display_text_color,
        [field]: target.upsellPage,
      },
    });

    await invalidateFunnel();
  };

  const handleOpenSheet = (upsell: Upsell) => {
    setSelectedUpsell(upsell);
    openModal('script-integration');
  };

  const handleEdit = (upsell: Upsell) => {
    setSelectedUpsell(upsell);
    openModal('funnel-edit-upsell');
  };

  const handleDelete = async (upsellId: string) => {
    try {
      await deleteUpsell({ productId: productId!, upsellId });
      closeModal('delete-upsell');
    } catch (error) {
      console.error('Error deleting upsell:', error);
    }
  };

  const handleUpdate = async (data: any) => {
    if (selectedUpsell) {
      await updateUpsell({
        productId: productId!,
        upsellId: selectedUpsell.id,
        data,
      });
      await invalidateFunnel();
    }
  };

  const formatRecurringInterval = (interval: string) => {
    const intervals: Record<string, string> = {
      daily: t('common:recurringIntervals.daily'),
      weekly: t('common:recurringIntervals.weekly'),
      monthly: t('common:recurringIntervals.monthly'),
      quarterly: t('common:recurringIntervals.quarterly'),
      semiannual: t('common:recurringIntervals.semiannual'),
      yearly: t('common:recurringIntervals.yearly'),
    };
    return intervals[interval as keyof typeof intervals] || '';
  };

  const headers = [
    { title: t('nome_tipo'), key: 'name_type' },
    { title: t('oferta'), key: 'offer' },
    { title: t('script'), key: 'script' },
    { title: '', key: 'actions' },
  ];

  return (
    <>
      <Card className='w-full mt-8 overflow-x-auto'>
        <div className='flex flex-col max-md:gap-3 md:flex-row w-full p-4 justify-between'>
          <div className='flex items-center gap-3'>
            <p className='text-base font-semibold'>{t('upsells')}</p>
          </div>

          <div className='flex items-center gap-2 flex-wrap justify-end'>
            {disconnectedUpsells.length > 0 && (
              <Button
                onClick={() => setShowDisconnected(true)}
                className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 text-xs font-medium text-amber-400 hover:bg-amber-500/10 transition-all"
              >
                <Unplug className="h-3.5 w-3.5" />
                <span>{t('funnel:disconnected_panel_btn', 'Desconectados')}</span>
                <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-black animate-pulse">
                  {disconnectedUpsells.length}
                </span>
              </Button>
            )}

            {canCreate && (
              <Button
                onClick={() =>
                  navigate(`/products/edit-product/${productId}/upsells/create`)
                }
              >
                + {t('criar_estrategia')}
              </Button>
            )}
          </div>
        </div>

        <TableListCustom
          emptyDescriptionMessage={t('emptyUpsells.description')}
          emptyIconMessage={<Upsells />}
          emptyTitleMessage={t('emptyUpsells.title')}
          headers={headers}
          isLoadingData={isLoading}
          data={upsells}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        >
          {upsells?.data?.map((upsell) => (
            <TableRow
              key={upsell.id}
              className='h-[64px] border-b border-b-colorBorder hover:bg-transparent'
            >
              <TableCell>
                <div className='flex flex-col'>
                  <span className='text-sm font-medium text-white'>
                    {upsell.name}
                  </span>
                  <span className='text-xs text-gray-400'>
                    {upsell.funnel_type === FunnelType.UpSell
                      ? 'Upsell'
                      : 'Downsell'}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <div className='flex flex-col'>
                  <span className='text-sm text-white'>
                    {upsell.offer.name}
                  </span>
                  <span className='text-xs text-gray-400'>
                    {upsell.offer.price_formatted}{' '}
                    {upsell.offer.payment_type === 'recurring' &&
                      formatRecurringInterval(upsell.offer.recurring_interval)}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                {canViewScript ? (
                  <Button
                    className='bg-[#27272A] hover:bg-[#27272A]'
                    size='sm'
                    onClick={() => handleOpenSheet(upsell)}
                  >
                    <span className='text-white'>{t('ver_script')}</span>
                    <Code2 size={15} className='text-[#A1A1AA]' />
                  </Button>
                ) : (
                  <span className='text-xs text-zinc-600'>—</span>
                )}
              </TableCell>
              <TableCell className='w-[50px]'>
                {(canEdit || canDelete) && (
                  <CTATable
                    items={[
                      ...(canEdit ? [{
                        id: 1,
                        label: t('edit'),
                        icon: <Edit />,
                        action: () => handleEdit(upsell),
                      }] : []),
                      ...(canDelete ? [{
                        id: 2,
                        label: t('delete'),
                        icon: <Trash color='#F87171' />,
                        action: () => {
                          setSelectedUpsell(upsell);
                          openModal('delete-upsell');
                        },
                      }] : []),
                    ]}
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableListCustom>
      </Card>

      {canDelete && (
        <DialogDelete
          isOpen={modals['delete-upsell']}
          onClose={() => closeModal('delete-upsell')}
          title={t('excluir_upsell_titulo')}
          description={t('excluir_upsell_descricao')}
          onDelete={() => handleDelete(selectedUpsell.id)}
        />
      )}

      {canViewScript && (
        <SideQuickScript
          upsell={selectedUpsell}
          isOpen={modals['script-integration']}
          onClose={() => closeModal('script-integration')}
        />
      )}

      {canEdit && (
        <CustomSheet
          open={modals['funnel-edit-upsell']}
          onClose={() => closeModal('funnel-edit-upsell')}
        >
          <EditUpsellSheet
            productId={productId!}
            upsell={selectedUpsell}
            onUpdate={handleUpdate}
            isLoading={isUpdating}
          />
        </CustomSheet>
      )}

      {canCustomizeButtons && (
        <CustomSheet
          open={modals['customize-buttons']}
          onClose={() => closeModal('customize-buttons')}
        >
          <CustomizeButtonsSheet
            upsell={selectedUpsell}
            onUpdate={handleUpdate}
            isLoading={isUpdating}
          />
        </CustomSheet>
      )}

      <DisconnectedUpsellsDialog
        isOpen={showDisconnected}
        onClose={() => setShowDisconnected(false)}
        disconnectedUpsells={disconnectedUpsells}
        connectedUpsells={connectedUpsells}
        onConnect={connectUpsells}
      />
    </>
  );
}
